import { useEffect, useId, useRef, useState } from 'react'

import { chartAssets } from '../../constants/assets'
import {
  revenueAxisTicks,
  revenueLineColor,
  type RevenueRange,
} from '../../data/revenue'
import { buildSmoothLinePath, formatRevenueK } from '../../utils/chartMath'
import { cn } from '../../utils/cn'

export type RevenuePlotPoint = {
  id: string
  label: string
  period: string
  valueK: number
  x: number
  y: number
}

type RevenueLineChartProps = {
  range: RevenueRange
  points: RevenuePlotPoint[]
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}

const LINE_STROKE = 2
const MARKER_HIT = 28

export const RevenueLineChart = ({
  range,
  points,
  activeIndex,
  onActiveIndexChange,
}: RevenueLineChartProps) => {
  const pathRef = useRef<SVGPathElement>(null)
  const [pathLength, setPathLength] = useState(0)
  const [drawn, setDrawn] = useState(false)
  const gradientId = useId()

  const linePath = buildSmoothLinePath(points)
  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x} 315 L ${points[0].x} 315 Z`
      : ''

  useEffect(() => {
    setDrawn(false)
    const path = pathRef.current
    if (!path) return

    const length = path.getTotalLength()
    setPathLength(length)

    const frame = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(frame)
  }, [range, linePath])

  return (
    <svg
      aria-label="Revenue line chart"
      className="pointer-events-none absolute inset-0 overflow-visible"
      role="img"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="123" y2="315" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={revenueLineColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={revenueLineColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      {areaPath ? (
        <path
          d={areaPath}
          fill={`url(#${gradientId})`}
          className={cn(
            'transition-opacity duration-500 motion-reduce:transition-none',
            drawn ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}

      <path
        ref={pathRef}
        d={linePath}
        fill="none"
        stroke={revenueLineColor}
        strokeWidth={LINE_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: pathLength || undefined,
          strokeDashoffset: drawn ? 0 : pathLength,
          transition: 'stroke-dashoffset 900ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        className="motion-reduce:!transition-none motion-reduce:!stroke-dashoffset-0"
      />

      {points.map((point, index) => {
        const isActive = index === activeIndex

        return (
          <g key={`${range}-${point.id}`} className="pointer-events-auto">
            <circle
              cx={point.x}
              cy={point.y}
              r={MARKER_HIT / 2}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => onActiveIndexChange(index)}
              onFocus={() => onActiveIndexChange(index)}
              tabIndex={0}
              role="button"
              aria-label={`${point.period}: ${formatRevenueK(point.valueK)}`}
            />

            <circle
              cx={point.x}
              cy={point.y}
              r={isActive ? 6 : 4}
              fill={isActive ? '#E8F5A0' : revenueLineColor}
              stroke={isActive ? '#FFFFFF' : 'transparent'}
              strokeWidth={isActive ? 2 : 0}
              className={cn(
                'transition-all duration-300 motion-reduce:transition-none',
                drawn ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
              )}
              style={{ transitionDelay: drawn ? `${120 + index * 70}ms` : '0ms' }}
            />
          </g>
        )
      })}

      {revenueAxisTicks.map((tick, index) => {
        const isActive = index === activeIndex
        const src = isActive ? chartAssets.dotAxisLit : chartAssets.dotAxisDim

        return (
          <foreignObject
            key={`axis-${index}`}
            x={tick.x - 3}
            y={310}
            width={8}
            height={8}
            className="pointer-events-none overflow-visible"
          >
            <img alt="" aria-hidden className="block size-full max-w-none" src={src} />
          </foreignObject>
        )
      })}
    </svg>
  )
}

/** Positions the tooltip callout centered above a plot point. */
export const revenueTooltipBox = (point: RevenuePlotPoint) => ({
  x: Math.min(Math.max(point.x - 38, 12), 385),
  y: Math.max(point.y - 58, 8),
  width: 76,
  height: 46,
})